import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import type {TaillightsController} from '@github-ui/taillights/controller'
import {useTaillightsController, useTaillightsRendering} from '@github-ui/taillights/provider'
import blockStyles from '@github-ui/taillights/styles'
import type {TaillightRenderer} from '@github-ui/taillights/types'
import {TaillightsSourceInteraction, type TaillightsSourceInteractionProps} from '@github-ui/taillights-symbols'
import type {NativeSelectionMap} from '@github-ui/taillights-symbols/native-selection'
import type {PreparedView} from '@github-ui/taillights-worker/document'
import {sourceChunks} from '@github-ui/taillights-worker/source-chunks'
import {testIdProps} from '@github-ui/test-id-props'
import {
  defaultRangeExtractor,
  observeWindowRect,
  type Range,
  useWindowVirtualizer,
  type Virtualizer,
} from '@github-ui/virtualizer'
import {type Ref, type RefObject, useCallback, useLayoutEffect, useMemo, useState} from 'react'

import {createBufferedViewport} from './buffered-viewport'
import {InverseStickySourceChunks} from './InverseStickySourceChunks'
import {windowedSourceChunkLines} from './source-window'
import {SourceChunk, sourceRenderingStyle} from './SourceChunk'
import styles from './TaillightsCodeView.module.css'
import type {GutterContentProps} from './TaillightsGutter'
import type {GutterFocus} from './use-gutter-focus'

export function WindowedSource({
  preparedFor,
  sourceLineCount,
  sourcePlaneRef,
  ...props
}: TaillightsSourceInteractionProps & {
  preparedFor: TaillightsController | null
  sourceLineCount: number
  sourcePlaneRef?: Ref<HTMLDivElement>
}) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const profile = preparedFor?.profile ?? controller?.profile
  const lineHeight = profile?.lineHeight ?? 20
  const renderingStyle = useMemo(() => sourceRenderingStyle(profile, rendering), [profile, rendering])
  return (
    <div
      {...testIdProps('taillights-code')}
      ref={sourcePlaneRef}
      className={blockStyles.Block}
      style={{
        ...renderingStyle,
        caretColor: profile?.defaultColor,
        width: props.view?.width ?? '100%',
        height: Math.max(lineHeight, props.view?.height ?? sourceLineCount * lineHeight),
      }}
    >
      <TaillightsSourceInteraction {...props} />
    </div>
  )
}

export type SourceChunksProps = Pick<TaillightsSourceInteractionProps, 'onRenderError'> & {
  view: PreparedView | null
  projection: NativeSelectionMap
  linesPerChunk: number
  renderer: TaillightRenderer
  safariCSSHighlighting: boolean
  gutter: GutterContentProps
  focus: GutterFocus
  scrollMargin: number
  scrollPaddingTop?: number
  virtualized: boolean
  preparedFor: TaillightsController | null
  linesToBuffer?: number
  plaintext?: boolean
  reverseStickyRefs?: {
    offsetRef: RefObject<HTMLDivElement | null>
    windowRef: RefObject<HTMLDivElement | null>
    sourceRef: RefObject<HTMLDivElement | null>
  }
}

export function SourceChunks(props: SourceChunksProps) {
  if (
    props.virtualized &&
    props.reverseStickyRefs &&
    props.linesPerChunk === windowedSourceChunkLines &&
    (props.linesToBuffer ?? 0) === 0 &&
    !props.plaintext
  ) {
    return <InverseStickySourceChunks {...props} reverseStickyRefs={props.reverseStickyRefs} />
  }
  return <LegacySourceChunks {...props} />
}

function LegacySourceChunks({
  view,
  projection,
  linesPerChunk,
  renderer,
  safariCSSHighlighting,
  gutter,
  focus,
  scrollMargin,
  virtualized,
  preparedFor,
  onRenderError,
  linesToBuffer,
  reverseStickyRefs,
  plaintext = false,
}: SourceChunksProps) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const {isBrowser} = useRuntimeEnvironment()
  const profile = preparedFor?.profile ?? controller?.profile
  const lineHeight = profile?.lineHeight ?? 20
  const bufferedViewport = useMemo(
    () => (linesToBuffer === undefined ? null : createBufferedViewport(linesToBuffer, lineHeight)),
    [linesToBuffer, lineHeight],
  )
  const fallbackStyle = useMemo(() => sourceRenderingStyle(profile, rendering), [profile, rendering])
  const blocks = useMemo(
    () => view?.blocks ?? sourceChunks(projection.lineNumbers, linesPerChunk),
    [view, projection.lineNumbers, linesPerChunk],
  )
  const [viewportHeight, setViewportHeight] = useState<number | null>(null)
  const {focusedLine, onFocusChange} = focus
  const ownerLine = gutter.actions.owner?.lineNumber
  const rangeExtractor = useCallback(
    (range: Range) => {
      let indexes = defaultRangeExtractor(range)
      const chunkHeight = (blocks[0]?.lineNumbers.length ?? 0) * lineHeight
      // Keep an extra chunk to avoid resizing the unfolded sticky slice at each boundary.
      if (
        reverseStickyRefs &&
        linesToBuffer === 0 &&
        viewportHeight !== null &&
        !view?.collapsed.length &&
        chunkHeight > 0
      ) {
        const count = Math.min(range.count, Math.ceil(viewportHeight / chunkHeight) + 1)
        const start = Math.min(range.startIndex, range.count - count)
        indexes = Array.from({length: count}, (_, index) => start + index)
      }
      // Only actual focus and an open overlay can retain an offscreen chunk.
      for (const line of [focusedLine, ownerLine]) {
        if (line == null || !projection.sourceToDisplay.has(line - 1)) continue
        const index = blocks.findIndex(block => block.lineNumbers.includes(line - 1))
        if (index >= 0 && !indexes.includes(index)) indexes.push(index)
      }
      return indexes.sort((a, b) => a - b)
    },
    [blocks, lineHeight, focusedLine, ownerLine, view, projection, reverseStickyRefs, linesToBuffer, viewportHeight],
  )
  const estimateSize = useCallback(
    (index: number) => Math.max(1, blocks[index]?.lineNumbers.length ?? 0) * lineHeight,
    [blocks, lineHeight],
  )
  const getItemKey = useCallback((index: number) => blocks[index]?.anchor ?? index, [blocks])
  const windowLocal = reverseStickyRefs !== undefined
  const directDomUpdates = virtualized && !windowLocal && view !== null
  const observeElementRect = useCallback<Virtualizer<Window, Element>['options']['observeElementRect']>(
    (instance, callback) => {
      const observeRect = bufferedViewport?.observeElementRect ?? observeWindowRect
      return observeRect(instance, rect => {
        if (windowLocal) {
          setViewportHeight(rect.height > 0 ? rect.height - (bufferedViewport?.bufferPixels ?? 0) * 2 : 0)
        }
        callback(rect)
      })
    },
    [bufferedViewport, windowLocal],
  )
  const {items, measure, measureElement, containerRef} = useWindowVirtualizer({
    count: blocks.length,
    estimateSize,
    getItemKey,
    enabled: virtualized && isBrowser,
    useFlushSync: false,
    directDomUpdates,
    directDomUpdatesMode: 'position',
    overscan: bufferedViewport ? 0 : 1,
    observeElementRect,
    scrollMargin: scrollMargin + (bufferedViewport?.bufferPixels ?? 0),
    rangeExtractor,
  })
  useLayoutEffect(() => {
    measure()
  }, [blocks, measure])
  const mounted = virtualized
    ? isBrowser && items.length
      ? items.map(item => item.index)
      : blocks.slice(0, 2).map((_, index) => index)
    : blocks.map((_, index) => index)
  const firstBlock = blocks[mounted[0] ?? -1]
  const lastBlock = blocks[mounted.at(-1) ?? -1]
  const renderedStart = firstBlock ? firstBlock.displayStart * lineHeight : 0
  const renderedHeight = Math.max(
    lineHeight,
    lastBlock ? (lastBlock.displayStart + lastBlock.lineNumbers.length) * lineHeight - renderedStart : 0,
  )
  useLayoutEffect(() => {
    return () => {
      const offset = reverseStickyRefs?.offsetRef.current
      const stickyWindow = reverseStickyRefs?.windowRef.current
      const sourcePlane = reverseStickyRefs?.sourceRef.current
      offset?.style.removeProperty('height')
      stickyWindow?.style.removeProperty('top')
      stickyWindow?.style.removeProperty('bottom')
      stickyWindow?.removeAttribute('data-sticky-ready')
      sourcePlane?.style.removeProperty('transform')
    }
  }, [reverseStickyRefs])
  useLayoutEffect(() => {
    const offset = reverseStickyRefs?.offsetRef.current
    const stickyWindow = reverseStickyRefs?.windowRef.current
    const sourcePlane = reverseStickyRefs?.sourceRef.current
    if (!offset || !stickyWindow || !sourcePlane || !isBrowser || viewportHeight === null) return
    const offsetHeight = `${renderedStart}px`
    const sourceTransform = `translateY(${-renderedStart}px)`
    const inset = `${Math.min(0, viewportHeight - renderedHeight)}px`
    const updateOffset = offset.style.height !== offsetHeight
    const updateSource = sourcePlane.style.transform !== sourceTransform
    const updateTop = stickyWindow.style.top !== inset
    const updateBottom = stickyWindow.style.bottom !== inset
    // Commit the slice's geometry together, without reading layout or deferring any writes.
    if (updateOffset) offset.style.setProperty('height', offsetHeight)
    if (updateSource) sourcePlane.style.setProperty('transform', sourceTransform)
    if (updateTop) stickyWindow.style.setProperty('top', inset)
    if (updateBottom) stickyWindow.style.setProperty('bottom', inset)
    stickyWindow.setAttribute('data-sticky-ready', 'true')
  }, [reverseStickyRefs, renderedStart, renderedHeight, viewportHeight, isBrowser])
  const preferredLine = focusedLine ?? ownerLine ?? (gutter.anchorRange ? gutter.anchorRange.start.line + 1 : null)
  const tabStopLine =
    preferredLine !== null && mounted.some(index => blocks[index]?.lineNumbers.includes(preferredLine - 1))
      ? preferredLine
      : (blocks[mounted[0] ?? -1]?.lineNumbers[0] ?? -1) + 1
  return (
    <div
      className={styles.WindowChunks}
      ref={directDomUpdates ? containerRef : undefined}
      style={{paddingBlockStart: !view && !windowLocal ? renderedStart : undefined}}
    >
      {mounted.map((index, position) => {
        const block = blocks[index]
        if (!block) return null
        const previousBlock = blocks[mounted[position - 1] ?? -1]
        const gapBefore =
          (windowLocal || !view) && previousBlock
            ? (block.displayStart - previousBlock.displayStart - previousBlock.lineNumbers.length) * lineHeight
            : undefined
        return (
          <SourceChunk
            key={block.anchor}
            ref={directDomUpdates ? measureElement : undefined}
            virtualIndex={index}
            directDomUpdates={directDomUpdates}
            windowLocal={windowLocal}
            gapBefore={gapBefore}
            layout={block}
            block={view?.blocks[index]}
            renderer={renderer}
            safariCSSHighlighting={safariCSSHighlighting}
            gutter={gutter}
            lineHeight={lineHeight}
            tabStopLine={block.lineNumbers.includes(tabStopLine - 1) ? tabStopLine : null}
            onFocusChange={onFocusChange}
            onRenderError={onRenderError}
            paintReady={!plaintext && controller !== null && preparedFor === controller}
            fallbackStyle={fallbackStyle}
            showGutter={isBrowser || virtualized || index === 0}
          />
        )
      })}
    </div>
  )
}
